
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Wallet,
  Download,
  Filter,
  Store,
  Calendar,
  CheckCircle,
  Printer as PrintIcon,
  RefreshCcw,
  Loader2,
  DollarSign,
  Info,
  FileText,
  ArrowDownCircle,
  ArrowUpCircle,
  Wallet as WalletIcon,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/data-table";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

export const DailyCashBalanceReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    date: "",
    opening_balance: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
  }));
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);



  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // ==================== QUERIES ====================
  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report daily cash balance view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  // ==================== MUTATION ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report daily cash balance view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};

      const dateObj = currentFilters.date
        ? new Date(currentFilters.date)
        : new Date();
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      params.date = `${year}-${month}-${day}`;
      if (
        currentFilters.opening_balance !== "" &&
        currentFilters.opening_balance !== null
      ) {
        params.opening_balance = parseFloat(currentFilters.opening_balance);
      }
      if (currentFilters.store_id) {
        params.store_id = currentFilters.store_id;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.DAILY_CASH_BALANCE,
        { params },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report daily cash balance view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};

      const dateObj = currentFilters.date
        ? new Date(currentFilters.date)
        : new Date();
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      params.date = `${year}-${month}-${day}`;
      if (
        currentFilters.opening_balance !== "" &&
        currentFilters.opening_balance !== null
      ) {
        params.opening_balance = parseFloat(currentFilters.opening_balance);
      }
      if (currentFilters.store_id) {
        params.store_id = currentFilters.store_id;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DAILY_CASH_BALANCE,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Daily cash balance report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate daily cash balance report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateReport = () => {
    showToast("Generating daily cash balance report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      date: "",
      opening_balance: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
    });
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `daily-cash-balance-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];



  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <Wallet className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Financial Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Daily Cash Balance Report
              </span>
            </div>
          }
          subtitle="Generate a comprehensive daily cash balance report for a specific date, opening balance, and store."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClearFilters}
            className="rounded-xl mr-2"
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report daily cash balance view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report daily cash balance view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-8">
        {/* Filter Panel */}
        <Card className="shadow-none border border-border/50 overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-xl">Report Parameters</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Specify the date, opening balance, and store for this report
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>
          {showFilters && (
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Date */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Report Date
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">
                      Date{" "}
                      <span className="text-xs text-muted-foreground font-normal">
                        (optional)
                      </span>
                    </Label>
                    <Input
                      type="date"
                      className="bg-muted/30 border-border focus:bg-background transition-colors"
                      name="date"
                      value={filters.date}
                      onChange={handleFilterChange}
                    />
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Leave blank to use today&apos;s date
                    </p>
                  </div>
                </div>

                {/* Opening Balance */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" /> Opening
                    Balance
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">
                      Opening Balance{" "}
                      <span className="text-xs text-muted-foreground font-normal">
                        (optional, ≥ 0)
                      </span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="bg-muted/30 border-border focus:bg-background transition-colors"
                      name="opening_balance"
                      value={filters.opening_balance}
                      onChange={handleFilterChange}
                    />
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      The cash balance at the start of the day
                    </p>
                  </div>
                </div>

                {/* Store */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Store className="h-4 w-4 text-primary" /> Store / Branch
                  </h3>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">
                      Store{" "}
                      <span className="text-xs text-muted-foreground font-normal">
                        (optional)
                      </span>
                    </Label>
                    <ReactSelect
                      options={storeOptions}
                      value={
                        storeOptions.find(
                          (opt) => opt.value === String(filters.store_id || ""),
                        ) ||
                        (filters.store_id &&
                          currentStore?.id &&
                          String(filters.store_id) === String(currentStore.id)
                          ? {
                            value: String(currentStore.id),
                            label: currentStore.name,
                          }
                          : null)
                      }
                      onChange={(opt) =>
                        setFilters((p) => ({
                          ...p,
                          store_id: opt?.value || "",
                        }))
                      }
                      placeholder="All Locations"
                      isClearable={Boolean(filters.store_id)}
                    />
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Filter by a specific store location
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && (
          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {previewData.summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Cash In
                      </p>
                      <h3 className="text-2xl font-bold text-success">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_cash_in) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-success/10 text-success rounded-xl">
                      <ArrowUpCircle className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Cash Out
                      </p>
                      <h3 className="text-2xl font-bold text-destructive">
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_cash_out) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-destructive/10 text-destructive rounded-xl">
                      <ArrowDownCircle className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Net Balance
                      </p>
                      <h3
                        className={`text-2xl font-bold ${parseFloat(previewData.summary.total_net_balance) >= 0 ? "text-success" : "text-destructive"}`}
                      >
                        {currencySymbol}
                        {(
                          parseFloat(previewData.summary.total_net_balance) || 0
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </h3>
                    </div>
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <WalletIcon className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-lg ring-1 ring-border/50">
                  <CardContent className="p-6 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Total Stores
                      </p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {previewData.summary.total_stores || 0}
                      </h3>
                    </div>
                    <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                      <Store className="h-6 w-6" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Detailed Breakdown */}
            {previewData.store_details &&
              previewData.store_details.length > 0 && (
                <div className="space-y-6 mt-8">
                  <h3 className="text-xl font-bold tracking-tight">
                    Detailed Store Analysis
                  </h3>
                  <Accordion
                    type="single"
                    collapsible
                    className="w-full space-y-4"
                  >
                    {previewData.store_details.map((store, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${index}`}
                        className="border rounded-xl bg-background shadow-sm overflow-hidden px-1"
                      >
                        <AccordionTrigger className="px-6 hover:no-underline hover:bg-muted/50 rounded-t-xl transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="bg-primary/10 p-2 rounded-lg">
                              <Store className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex flex-col text-left">
                              <span className="font-bold text-base">
                                {store.store_name}
                              </span>
                              <span className="text-xs text-muted-foreground mt-1 font-normal">
                                Closing Cash: {currencySymbol}
                                {(
                                  parseFloat(
                                    store.cash_drawer?.closing_balance,
                                  ) || 0
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-6 pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Cash Drawer */}
                            <Card className="shadow-none border border-border/50 bg-muted/20">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-bold flex items-center gap-2 text-primary">
                                  <Wallet className="h-4 w-4" />
                                  Cash Drawer
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      Total Cash In
                                    </span>
                                    <span className="font-medium text-success">
                                      {currencySymbol}
                                      {(
                                        parseFloat(
                                          store.cash_drawer?.total_cash_in,
                                        ) || 0
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      Total Cash Out
                                    </span>
                                    <span className="font-medium text-destructive">
                                      {currencySymbol}
                                      {(
                                        parseFloat(
                                          store.cash_drawer?.total_cash_out,
                                        ) || 0
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                  <div className="pt-2 border-t flex justify-between text-sm font-bold">
                                    <span>Closing Balance</span>
                                    <span>
                                      {currencySymbol}
                                      {(
                                        parseFloat(
                                          store.cash_drawer?.closing_balance,
                                        ) || 0
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                      })}
                                    </span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>

                            {/* Top Products */}
                            <Card className="shadow-none border border-border/50 bg-muted/20">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-bold flex items-center gap-2 text-primary">
                                  <Info className="h-4 w-4" />
                                  Top Products Sold
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                {store.top_products?.length > 0 ? (
                                  <div className="space-y-3">
                                    {store.top_products.map((p, i) => (
                                      <div
                                        key={i}
                                        className="flex justify-between items-center text-sm"
                                      >
                                        <div className="flex flex-col">
                                          <span className="font-medium truncate max-w-[200px]">
                                            {p.name}
                                          </span>
                                          <span className="text-xs text-muted-foreground">
                                            Qty: {p.quantity}
                                          </span>
                                        </div>
                                        <span className="font-bold">
                                          {currencySymbol}
                                          {(
                                            parseFloat(p.total) || 0
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground italic">
                                    No products sold.
                                  </p>
                                )}
                              </CardContent>
                            </Card>

                            {/* Payment Details */}
                            <Card className="shadow-none border border-border/50 bg-muted/20">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-bold flex items-center gap-2 text-primary">
                                  <DollarSign className="h-4 w-4" />
                                  Payment Details
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                {store.payment_details?.length > 0 ? (
                                  <div className="space-y-2">
                                    {store.payment_details.map((p, i) => (
                                      <div
                                        key={i}
                                        className="flex justify-between items-center text-sm border-b border-border/30 pb-2 last:border-0 last:pb-0"
                                      >
                                        <span className="text-muted-foreground capitalize">
                                          {p.method}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                            {p.status}
                                          </span>
                                          <span className="font-medium text-success">
                                            {currencySymbol}
                                            {(
                                              parseFloat(p.amount) || 0
                                            ).toLocaleString(undefined, {
                                              minimumFractionDigits: 2,
                                            })}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground italic">
                                    No payments recorded.
                                  </p>
                                )}
                              </CardContent>
                            </Card>

                            {/* Sales by Category */}
                            <Card className="shadow-none border border-border/50 bg-muted/20">
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-bold flex items-center gap-2 text-primary">
                                  <CheckCircle className="h-4 w-4" />
                                  Sales by Category
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                {store.sales_by_category?.length > 0 ? (
                                  <div className="space-y-2">
                                    {store.sales_by_category.map((c, i) => (
                                      <div
                                        key={i}
                                        className="flex justify-between text-sm border-b border-border/30 pb-2 last:border-0 last:pb-0"
                                      >
                                        <span className="text-muted-foreground">
                                          {c.category} (x{c.quantity})
                                        </span>
                                        <span className="font-medium">
                                          {currencySymbol}
                                          {(
                                            parseFloat(c.total) || 0
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                          })}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground italic">
                                    No category sales.
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
          </div>
        )}
      </div>

      {/* Success Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Generated!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The Daily Cash Balance Report is ready. Choose an action
                  below.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
